import Alert from "@mui/material/Alert";
import { styled } from "../../../../../lib/styled";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import type { CorpusFeedComposition } from "@shared/types/corpus/api.ts";
import { CorpusFeedCompositionView } from "../../../../../lib/corpusFeedCompositionView";
import { useTaxonomy } from "../../../../../context/TaxonomyProvider";
import type { AsyncDataResult } from "../../../../../hooks/useAsyncData";
import { Facts } from "../../../../../components/primitives/data-display/Facts";
import { gaps } from "../../../../../components/primitives/spacing";
import { COMPOSITION_TABS } from "../feedDetailFilters";
import { FeedFactsSkeleton } from "./FeedFactsSkeleton";

type CompositionTab = (typeof COMPOSITION_TABS)[number]["value"];

// A fixed size, so the panel keeps its shape whether the list is short, long or still loading.
const ScrollArea = styled("div")(({ theme }) => ({
  height: theme.spacing(24),
  overflowY: "auto",
}));

interface FeedCompositionProps {
  composition: AsyncDataResult<CorpusFeedComposition>;
  /** The readable name of each subject, by subject; a subject without one is shown by its id. */
  subjectNames: Record<string, string>;
  tab: CompositionTab;
  onTabChange: (tab: CompositionTab) => void;
}

/** What a feed's papers are made of: those in the corpus, by health field and by subject. It loads
 *  on its own, so the page does not wait for it; it is a part of the panel it sits in. */
export function FeedComposition({
  composition,
  subjectNames,
  tab,
  onTabChange,
}: FeedCompositionProps) {
  const taxonomy = useTaxonomy();
  const view = composition.data ? new CorpusFeedCompositionView(composition.data, taxonomy) : null;

  return (
    <Stack gap={gaps.snug}>
      <Tabs
        value={tab}
        onChange={(_event, value: CompositionTab) => onTabChange(value)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {COMPOSITION_TABS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      <ScrollArea>
        {composition.loading ? (
          <FeedFactsSkeleton rows={3} />
        ) : composition.error ? (
          <Alert severity="warning">{composition.error}</Alert>
        ) : view === null ? null : tab === "fields" ? (
          view.isEmpty ? (
            <Typography variant="body2" color="text.secondary">
              No papers from this feed have a health field yet.
            </Typography>
          ) : (
            <Facts
              items={view.fields.map((field) => ({ label: field.name, value: field.papersText }))}
            />
          )
        ) : view.hasNoSubjects ? (
          <Typography variant="body2" color="text.secondary">
            No papers from this feed have a subject yet.
          </Typography>
        ) : (
          <Facts
            items={view
              .subjects(subjectNames)
              .map((subject) => ({ label: subject.name, value: subject.papersText }))}
          />
        )}
      </ScrollArea>
    </Stack>
  );
}
